// Modules M01 à M25 : activation et désactivation avec confirmation, explication des effets
// (H9-01) et motif obligatoire.
import { Bandeau, Bouton, DialogueConfirmation, PuceEtat, ZoneTexte } from '@cc/ui';
import { useState } from 'react';
import { requete } from '../../api/client';
import { useModules } from '../../api/requetes';
import type { ModuleEtat } from '../../api/types';
import { EnTetePage } from '../../commun/EnTetePage';
import { EtatRequete } from '../../commun/EtatRequete';
import { date } from '../../commun/format';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import styles from './administration.module.css';

const EFFETS_DESACTIVATION =
  'Les écrans du module disparaissent de la navigation et les nouvelles créations sont bloquées. Les dossiers existants restent consultables (recherche, archives) et les traitements engagés (créances, échéances) se poursuivent jusqu\'à leur terme.';
const EFFETS_ACTIVATION = 'Les écrans du module apparaissent dans la navigation des agents qui ont les droits correspondants, et ses indicateurs sont calculés.';

export function PageModules() {
  const { contexte } = useSession();
  const modules = useModules();
  const [cible, setCible] = useState<ModuleEtat | null>(null);
  const [motif, setMotif] = useState('');
  const administrer = peut(contexte, 'parametres', 'administrer');
  const modification = useAction({
    executer: (m: ModuleEtat) => requete<ModuleEtat>('/modules', { methode: 'PUT', corps: { module: m.module, actif: !m.actif, motif } }),
    succes: (r) => `Module ${r.module} ${r.actif ? 'activé' : 'désactivé'}.`,
    // Le contexte porte les modules actifs (navigation, écrans de création) : il est relu aussi.
    invalider: [['modules'], ['audit'], ['contexte']],
    apres: () => {
      setCible(null);
      setMotif('');
    },
  });

  const actifs = (modules.data ?? []).filter((m) => m.actif).length;
  return (
    <>
      <EnTetePage
        etiquette="Administration"
        titre="Modules"
        description={modules.data ? `${actifs} module${actifs > 1 ? 's' : ''} actif${actifs > 1 ? 's' : ''} sur ${modules.data.length}.` : undefined}
      />
      {!administrer ? <Bandeau type="info">Votre rôle permet de consulter l&apos;état des modules, pas de le modifier.</Bandeau> : null}
      <EtatRequete chargement={modules.isPending} erreur={modules.error} onReessayer={() => void modules.refetch()} fonction="La liste des modules">
        <section aria-label="Modules" style={{ border: '1px solid var(--cc-ligne)', borderRadius: 8, background: 'var(--cc-surface)' }}>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(modules.data ?? []).map((m) => (
              <li key={m.module} className={styles.module}>
                <span className={styles.code}>{m.module}</span>
                <span>
                  {m.libelle}
                  <small>
                    {m.actif
                      ? `Actif depuis le ${date(m.active_le)}`
                      : m.desactive_le
                        ? `Désactivé le ${date(m.desactive_le)}`
                        : 'Jamais activé'}
                    {m.motif ? `, motif : ${m.motif}` : ''}
                    {m.actif_plateforme === false ? ' Désactivé sur la plateforme par le super administrateur : sans effet ici tant qu’il n’est pas réactivé.' : ''}
                  </small>
                </span>
                <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  {m.actif ? <PuceEtat ton="ok" libelle="Actif" /> : <PuceEtat ton="neutre" libelle="Non activé" />}
                  {administrer ? (
                    <Bouton petit variante={m.actif ? 'secondaire' : 'principal'} onClick={() => setCible(m)} aria-label={`${m.actif ? 'Désactiver' : 'Activer'} ${m.module} ${m.libelle}`}>
                      {m.actif ? 'Désactiver' : 'Activer'}
                    </Bouton>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </EtatRequete>
      <DialogueConfirmation
        ouvert={cible !== null}
        titre={cible ? `${cible.actif ? 'Désactiver' : 'Activer'} ${cible.module}` : ''}
        message={
          cible ? (
            <>
              <p>
                <strong>{cible.libelle}</strong>
              </p>
              <p>{cible.actif ? EFFETS_DESACTIVATION : EFFETS_ACTIVATION}</p>
            </>
          ) : null
        }
        libelleConfirmer={cible?.actif ? 'Désactiver le module' : 'Activer le module'}
        variante={cible?.actif ? 'danger' : 'principal'}
        enCours={modification.enCours}
        confirmationDesactivee={motif.trim().length < 3}
        onConfirmer={() => cible && modification.lancer(cible)}
        onAnnuler={() => {
          setCible(null);
          setMotif('');
          modification.reinitialiser();
        }}
      >
        <ZoneTexte libelle="Motif" aide="Trois caractères au moins ; conservé au journal" value={motif} onChange={(e) => setMotif(e.target.value)} required />
        {modification.messageErreur ? <Bandeau type="erreur">{modification.messageErreur}</Bandeau> : null}
      </DialogueConfirmation>
    </>
  );
}
